export const UI_PLAN_TO_API = {
  Basic: 'BASIC',
  Standard: 'STANDARD',
  'Visibility+': 'VISIBILITY_PLUS',
  Premium: 'PREMIUM',
};

export const API_PLAN_TO_UI = {
  BASIC: 'Basic',
  STANDARD: 'Standard',
  VISIBILITY_PLUS: 'Visibility+',
  PREMIUM: 'Premium',
};

export const UI_STATUS_TO_API = {
  active: 'ACTIVE',
  finished: 'EXPIRED',
  scheduled: 'SCHEDULED',
  pending: 'PENDING_PAYMENT',
  cancelled: 'CANCELLED',
};

export const formatPromotionDate = (isoDate) => {
  if (!isoDate) return '-';
  const date = new Date(isoDate);
  return date.toLocaleDateString('pl-PL');
};

export const formatPromotionPeriod = (startsAt, endsAt) => {
  if (startsAt && endsAt) {
    return `${formatPromotionDate(startsAt)} – ${formatPromotionDate(endsAt)}`;
  }
  if (startsAt) {
    return `od ${formatPromotionDate(startsAt)}`;
  }
  if (endsAt) {
    return `do ${formatPromotionDate(endsAt)}`;
  }
  return '—';
};

export const formatDateForInput = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
};

export const UI_PLAN_DURATION_DAYS = {
  Basic: 7,
  Standard: 14,
  'Visibility+': 21,
  Premium: 30,
};

export const formatDateForDisplay = (dateInput) => {
  if (!dateInput) return '';
  const normalized = formatDateForInput(dateInput);
  if (!normalized) return '';
  const [year, month, day] = normalized.split('-');
  return `${day}/${month}/${year}`;
};

export const parseDisplayDateToInput = (displayValue) => {
  const trimmed = (displayValue || '').trim();
  if (!trimmed) return '';

  const match = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (!match) return null;

  const day = match[1].padStart(2, '0');
  const month = match[2].padStart(2, '0');
  const year = match[3];
  const candidate = `${year}-${month}-${day}`;
  const test = new Date(`${candidate}T12:00:00`);
  if (Number.isNaN(test.getTime())) return null;
  if (formatDateForInput(test.toISOString()) !== candidate) return null;
  return candidate;
};

export const getPromotionPeriodRange = (startDateInput, durationDays, eventEndDate) => {
  if (!startDateInput || !durationDays) return null;

  const start = formatDateForInput(startDateInput);
  const endDate = new Date(`${start}T12:00:00`);
  endDate.setDate(endDate.getDate() + durationDays - 1);

  let end = formatDateForInput(endDate.toISOString());
  if (eventEndDate) {
    const capped = formatDateForInput(eventEndDate);
    if (capped && end > capped) {
      end = capped;
    }
  }

  return { start, end };
};

export const enumerateDateInputsInRange = (startInput, endInput) => {
  if (!startInput || !endInput) return [];

  const dates = [];
  const cursor = new Date(`${startInput}T12:00:00`);
  const end = new Date(`${endInput}T12:00:00`);

  while (cursor <= end) {
    dates.push(formatDateForInput(cursor.toISOString()));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
};

export const getTodayForDateInput = () => formatDateForInput(new Date().toISOString());

export const isEventEligibleForPromotion = (event) => {
  if (!event) return false;
  if (!event.isApprovedByAdmin) return false;
  if (!event.isActive) return false;
  if (event.endDate) {
    const eventEndMs = new Date(event.endDate).getTime();
    if (!Number.isNaN(eventEndMs) && eventEndMs <= Date.now()) {
      return false;
    }
  }
  return true;
};

export const filterPromotableEvents = (events) => (
  Array.isArray(events) ? events.filter(isEventEligibleForPromotion) : []
);

export const isDateWithinPromotionStartBounds = (dateInput, minDate, maxDate) => {
  if (!dateInput) return true;
  if (minDate && dateInput < minDate) return false;
  if (maxDate && dateInput > maxDate) return false;
  return true;
};

export const getPromotionStartDateBounds = (event = null) => {
  const min = getTodayForDateInput();
  let max = null;

  if (event?.endDate) {
    const eventEnd = formatDateForInput(event.endDate);
    if (eventEnd) {
      max = eventEnd;
    }
  }

  if (max && max < min) {
    return { min, max: null };
  }

  return { min, max };
};

export const validatePromotionSchedule = (startDate, event, durationDays) => {
  if (!durationDays) {
    return null;
  }

  const effectiveStart = startDate ? formatDateForInput(startDate) : getTodayForDateInput();
  if (!effectiveStart) {
    return 'Podaj prawidłową datę rozpoczęcia promowania.';
  }

  const today = getTodayForDateInput();
  if (effectiveStart < today) {
    return 'Data rozpoczęcia promowania nie może być w przeszłości.';
  }

  if (event?.endDate) {
    const eventEnd = formatDateForInput(event.endDate);
    if (eventEnd && effectiveStart > eventEnd) {
      return 'Nie można promować wydarzenia po jego zakończeniu';
    }
  }

  const range = getPromotionPeriodRange(effectiveStart, durationDays, event?.endDate);
  if (!range?.end || range.end < range.start) {
    return 'Pozostały czas wydarzenia jest za krótki na wybrany plan promowania';
  }
  if (range.end < today) {
    return 'Okres promowania nie może kończyć się w przeszłości.';
  }

  return null;
};

export const getPromotionTruncationNotice = (startDate, durationDays, eventEndDate) => {
  if (!durationDays || !eventEndDate) {
    return null;
  }

  const effectiveStart = startDate ? formatDateForInput(startDate) : getTodayForDateInput();
  if (!effectiveStart) {
    return null;
  }

  const capped = getPromotionPeriodRange(effectiveStart, durationDays, eventEndDate);
  const full = getPromotionPeriodRange(effectiveStart, durationDays);
  if (!capped?.end || !full?.end || capped.end >= full.end) {
    return null;
  }

  return `Pakiet trwa ${durationDays} dni, ale promowanie zakończy się ${formatPromotionDate(eventEndDate)} — pozostałe dni nie będą wykorzystane.`;
};

export const validatePromotionStartDate = (startDate, event, durationDays) => (
  validatePromotionSchedule(startDate, event, durationDays)
);

export const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PK ||
  'pk_test_51RqGwMRhCrrvDMQST1bFZSnRAPXKn8PNdWjUuRZP1c7QLngwywi4yxFKEYItxKymDFPufpeqE07Ca2ODKMtGXQnq00YBquhRsV';
